import { TitleButton } from '@/common/buttons/TitleButton';
import { useDialog } from '@/common/dialog/DialogContext';
import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { Mode } from '@/common/entityFramework/enumerations/Mode';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { ObjUtils } from '@/common/utils/ObjUtils';
import { CalcOvrdDialog } from '@/gp/entities/calcProd/CalcOvrdDialog';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton } from '@mui/material';
import { useCallback, useRef, useState } from 'react';

/**
 * Per requirement GP-10, this provides a list of calculation products
 **/
export const CalcProdList = ({ calcObj }) => {
  const { navigateTo } = useNavState();
  const rsrc = useRsrc();

  const [calcProdRows, setCalcProdRows] = useState([]);

  const { openDialog } = useDialog();

  const entityListRef = useRef();

  const isRepro = calcObj.src !== 'PER';
  const isBundle = calcObj.polType === 'BUNDLE';
  const isEst = ['EST_BPI', 'EST_BPA'].includes(calcObj.polType);

  const [isSrcCalc, setIsSrcCalc] = useState(false);

  const clientSideRowData = useCallback((setDataSource) => {
    const rowsReq = {
      expr: ExprUtils.whereParams('calcId', calcObj.id),
      sortModel: [{ colId: 'code', sort: 'asc' }]
    }

    rsrc.rows('gp', 'CALC_PROD', rowsReq).then((rows) => {
      for (const calcProd of rows) {
        if (calcProd.valStatus === 'NONE' & calcObj.calcSrc === 'PER') {
          // Not validated therefore no Val Results to handle
          continue;
        }

        const valRes = JSON.parse(calcProd.valRes);
        if (valRes != null) {
          const valResMap =
            valRes.MATCH_PROD ||
            valRes.NO_MATCH_CALC_NO_OVRD ||
            valRes.MATCH_CALC_NO_MATCH_OVRD ||
            valRes.NO_MATCH_CALC_NO_MATCH_OVRD ||
            valRes.NO_MATCH_CALC_MATCH_OVRD;

          if (valResMap != null) {
            calcProd.srcCalcValue = valResMap[0]?.srcCalcValue;
            calcProd.srcOvrdValue = valResMap[0]?.srcOvrdValue;
          }
        }
      }
      setDataSource(rows);

      if (!isRepro && calcObj.status === 'DRAFT') {
        // If values can be overriden, we store the rows to pass to the dialog
        setCalcProdRows(rows);
      }
    });
  }, []);

  const augTabButtons = (buttons) => {
    ObjUtils.clearObj(buttons)

    if (isRepro) {
      // Calc Src Button
      buttons.push(
        <IconButton
          color={isSrcCalc ? 'primary' : 'default'}
          onClick={(e) => {
            const gridApiRef = entityListRef.current.gridRef.current.gridApiRef;
            const newSrcCalcValue = !isSrcCalc;
            setIsSrcCalc(newSrcCalcValue);
            gridApiRef.current.setColumnsVisible(['srcCalcValue', 'srcOvrdValue'], newSrcCalcValue);
            gridApiRef.current.sizeColumnsToFit();
          }}>
          <FontAwesomeIcon icon={fas.faSearchDollar} />
        </IconButton>
      )
    } else if (calcObj.status === 'DRAFT') {
      // Override Calculations Toggle
      buttons.push(
        <TitleButton
          dataTestId={'calc-ovrd-button'}
          icon={fas.faPencil}
          toolTip="Override Values"
          onClick={openCalcOvrdDialog}
        />
      )
    }
  };


  const openCalcOvrdDialog = () => {
    const data = {
      calc: calcObj,
      entityEntryPoint: 'CALC_PROD',
      calcProdIds: calcProdRows.map((calcProd) => calcProd.id)
    }
    openDialog(CalcOvrdDialog, { data })
  }

  const augFieldDefs = useCallback((fieldToFieldDef) => {
    const columns = ['status', 'valStatus', 'ndc11ProdId', 'name', 'isActive'];
    fieldToFieldDef.status.maxWidth = 150;
    fieldToFieldDef.valStatus.maxWidth = 225;

    fieldToFieldDef.ndc11ProdId.hasName = false;
    fieldToFieldDef.ndc11ProdId.displayName = 'NDC11';
    fieldToFieldDef.ndc11ProdId.maxWidth = 200;

    fieldToFieldDef.isActive.cellRendererCode = 'BOOLEAN';
    fieldToFieldDef.isActive.cellClass = 'ag-center-cell';
    fieldToFieldDef.isActive.maxWidth = 150;

    if (!isBundle) {
      if (isEst) {
        columns.push('estimatedLineCount');
      } else {
        columns.push('value', 'ovrdValue', 'ovrdComment', 'srcCalcValue', 'srcOvrdValue')

        // Ensure currency formatting with exactly six decimal places
        fieldToFieldDef.value.type = 'MONEY';
        fieldToFieldDef.value.decimalPrecision = 6;

        fieldToFieldDef.ovrdValue.type = 'MONEY';
        fieldToFieldDef.ovrdValue.decimalPrecision = 6;

        fieldToFieldDef.ovrdValue.hide = isRepro;
        fieldToFieldDef.ovrdComment.hide = isRepro;
        fieldToFieldDef.srcCalcValue = {
          field: 'srcCalcValue',
          displayName: 'Source Calculation',
          initialHide: true,
          type: 'MONEY',
          decimalPrecision: 6,
        }
        fieldToFieldDef.srcOvrdValue = {
          field: 'srcOvrdValue',
          displayName: 'Source Override',
          initialHide: true,
          type: 'MONEY',
          decimalPrecision: 6,
        }
      }
    }

    Object.keys(fieldToFieldDef).forEach(colName => {
      if (!columns.includes(colName)) {
        delete fieldToFieldDef[colName];
      } else {
        const index = columns.indexOf(colName);
        fieldToFieldDef[colName].precedence = (index + 1) * 100
      }
    });
  });

  const getContextMenuItems = (params) => {
    const contextMenuItems = [
      {
        name: 'Details',
        icon: fas.faInfoCircle,
        action: () => navigateTo('gp/calc-prod', { mode: Mode.DETAIL, entityId: params.node.data.id, calc: calcObj })
      }
    ]

    if (isEst) {
      const key = {
        calcId: calcObj.id,
        prodCodes: [params.node.data.code]
      }

      contextMenuItems.push({
        name: 'View Estimated Transactions',
        icon: fas.faSignInAlt,
        action: () => navigateTo('gp/rec-viewer', { viewerCode: 'EST_CALC_TRX', entity: 'CALC_PROD', key: JSON.stringify(key) })
      })
    }

    return contextMenuItems;
  }

  return (
    <EntityList
      entity="CALC_PROD"
      clientSideRowData={clientSideRowData}
      augFieldDefs={augFieldDefs}
      augTabButtons={augTabButtons}
      ref={entityListRef}
      gridOptions={{
        getContextMenuItems: getContextMenuItems
      }}
    />
  )
}